import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Check,
  ChevronRight,
  Info,
  Lock,
  Minus,
  Plus,
  Ruler,
  ShieldCheck,
} from 'lucide-react'
import type { Product } from '@/services/products'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { cn } from '@/lib/utils'
import { normalizeStorefrontProduct, type NormalizedProduct } from '@/core/product'
import {
  getVariantAttributeValue,
  sanitizeStorefrontSelection,
  storefrontOptionValuesEqual,
} from '@/core/attributes'
import { resolveVariant, variantAttributesMatchSelection } from '@/core/variant'
import { addToCart } from '@/core/cart'
import { catalogProductToRawStorefront } from '@/storefront/lib/catalogProductToRaw'
import { listProductDisplayPrice } from '@/storefront/lib/listPrice'
import { formatTnd } from '@/storefront/lib/formatMoney'
import { useStorefrontBase } from '@/storefront/hooks/useStorefrontBase'
import { useOptionalReferenceStore } from '@/storefront/context/ReferenceStoreContext'
import { Button } from '@/storefront/components/Button'
import { selectableVariantCount } from '@/storefront/lib/variantChoice'
import { computeDiscount } from '@/lib/compareAtPrice'
import { detectColorOption, trustedHexFromVariants } from '@/lib/colorVariants'
import { ColorSwatchSelector } from '@/storefront/components/ColorSwatchSelector'

function cleanAttrs(attrs: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(attrs).filter(
      ([k, v]) => !k.startsWith('_') && v != null && String(v).trim() !== '',
    ),
  )
}

function canLineItemAdd(
  normalized: NormalizedProduct,
  resolved: ReturnType<typeof resolveVariant>
): boolean {
  if (!resolved.ok) return false
  return (
    normalized.isDropshipping ||
    !normalized.trackInventory ||
    (normalized.hasVariants && resolved.variant
      ? resolved.variant.status === 'active' &&
        (resolved.variant.inventoryQuantity ?? resolved.variant.stock ?? 0) > 0
      : normalized.stock > 0 && normalized.status !== 'out_of_stock')
  )
}

function galleryUrls(product: Product): string[] {
  const primary = (product.cover_image || product.image || '').trim()
  const rest = Array.isArray(product.images)
    ? product.images.map((u) => String(u ?? '').trim()).filter(Boolean)
    : []
  const out: string[] = []
  if (primary) out.push(primary)
  for (const u of rest) {
    if (!out.includes(u)) out.push(u)
  }
  return out
}

function sortOptionKeys(keys: string[]): string[] {
  const priority = ['size', 'taille', 'couleur', 'color', 'style']
  return [...keys].sort((a, b) => {
    const ia = priority.indexOf(a.toLowerCase())
    const ib = priority.indexOf(b.toLowerCase())
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    }
    return a.localeCompare(b)
  })
}

function allOptionKeys(normalized: NormalizedProduct): string[] {
  const keys = new Set<string>()
  for (const v of normalized.variants) {
    if (v.status === 'archived' || v.status === 'inactive') continue
    for (const k of Object.keys(v.attributes)) {
      if (!k.startsWith('_')) keys.add(k)
    }
  }
  return sortOptionKeys([...keys])
}

function initialSelection(normalized: NormalizedProduct): Record<string, string> {
  const r = resolveVariant(normalized, {})
  if (!r.ok || !r.variant) return {}
  return sanitizeStorefrontSelection(cleanAttrs({ ...r.variant.attributes }))
}

function mergeSelectionOnPick(
  normalized: NormalizedProduct,
  prev: Record<string, string>,
  changeKey: string,
  changeValue: string
): Record<string, string> {
  const active = normalized.variants.filter((v) => v.status === 'active')
  const prevClean = sanitizeStorefrontSelection(prev)
  const tryMap = { ...prevClean, [changeKey]: changeValue }
  const exact = active.find((v) =>
    variantAttributesMatchSelection((v.attributes || {}) as Record<string, string>, tryMap),
  )
  if (exact) return sanitizeStorefrontSelection(cleanAttrs(exact.attributes))

  const candidates = active.filter((v) =>
    storefrontOptionValuesEqual(getVariantAttributeValue((v.attributes || {}) as Record<string, string>, changeKey), changeValue),
  )
  if (!candidates.length) return prev

  let best = candidates[0]
  let bestScore = -1
  const keys = Object.keys(prevClean).filter((k) => k !== changeKey)
  for (const v of candidates) {
    let score = 0
    const attrs = (v.attributes || {}) as Record<string, string>
    for (const k of keys) {
      if (prevClean[k] && storefrontOptionValuesEqual(getVariantAttributeValue(attrs, k), prevClean[k])) score++
    }
    if (score > bestScore) {
      bestScore = score
      best = v
    }
  }
  return sanitizeStorefrontSelection(cleanAttrs(best.attributes))
}

function availableValuesForKey(
  normalized: NormalizedProduct,
  current: Record<string, string>,
  targetKey: string
): string[] {
  const active = normalized.variants.filter((v) => v.status === 'active')
  const cur = sanitizeStorefrontSelection(current)
  const keys = Object.keys(cur).filter((k) => k !== targetKey)
  const vals = new Set<string>()
  for (const v of active) {
    const attrs = (v.attributes || {}) as Record<string, string>
    let ok = true
    for (const k of keys) {
      if (cur[k] && !storefrontOptionValuesEqual(getVariantAttributeValue(attrs, k), cur[k])) {
        ok = false
        break
      }
    }
    const tv = getVariantAttributeValue(attrs, targetKey)
    if (ok && tv) vals.add(tv)
  }
  return [...vals].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

function useDrawerSide(): 'bottom' | 'right' {
  const [side, setSide] = useState<'bottom' | 'right'>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
      ? 'bottom'
      : 'right'
  )

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const apply = () => setSide(mq.matches ? 'bottom' : 'right')
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  return side
}

const motionReduceOverlay =
  'motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none'
const motionReducePanel =
  'motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none motion-reduce:!transition-none'

const staggerIn =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-reduce:animate-none motion-reduce:opacity-100'

function QuickViewSkeleton({ side }: { side: 'bottom' | 'right' }) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      aria-busy="true"
      aria-live="polite"
      aria-label="Chargement du produit"
    >
      <div
        className={cn(
          'relative aspect-[3/4] w-full bg-muted sm:aspect-[4/5]',
          staggerIn
        )}
      >
        <div className="absolute inset-0 animate-pulse bg-muted/80 motion-reduce:animate-none" />
      </div>
      <div
        className={cn(
          'space-y-4 px-5 py-5',
          side === 'bottom' && 'pb-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:pb-5'
        )}
      >
        <div className={cn('space-y-2', staggerIn, 'motion-safe:delay-75')}>
          <div className="h-2 w-20 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-6 w-[85%] max-w-sm animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-4 w-full animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        </div>
        <div className={cn('flex gap-2', staggerIn, 'motion-safe:delay-150')}>
          <div className="h-12 flex-1 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        </div>
        <div className={cn('h-12 w-full animate-pulse rounded bg-foreground/20 motion-reduce:animate-none', staggerIn, 'motion-safe:delay-200')} />
      </div>
    </div>
  )
}

type CartPhase = 'idle' | 'loading' | 'success'

function ProductQuickViewDrawerBody({
  product,
  side,
  onClose,
}: {
  product: Product
  side: 'bottom' | 'right'
  onClose: () => void
}) {
  const base = useStorefrontBase()
  const navigate = useNavigate()
  const refStore = useOptionalReferenceStore()

  const normalized = useMemo(
    () => normalizeStorefrontProduct(catalogProductToRawStorefront(product)),
    [product]
  )

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({})
  const [imageIdx, setImageIdx] = useState(0)
  const [qty, setQty] = useState(1)
  const [cartPhase, setCartPhase] = useState<CartPhase>('idle')

  useEffect(() => {
    setSelectedOptions(initialSelection(normalized))
    setImageIdx(0)
    setQty(1)
    setCartPhase('idle')
  }, [product.id, normalized])

  const resolved = useMemo(
    () => resolveVariant(normalized, selectedOptions),
    [normalized, selectedOptions]
  )

  const optionKeys = useMemo(() => allOptionKeys(normalized), [normalized])
  const needsVariantChoiceUI = selectableVariantCount(normalized.variants) > 1

  const urls = useMemo(() => {
    const baseUrls =
      normalized.images.length > 0 ? [...normalized.images] : galleryUrls(product)
    const vImg = resolved.ok && resolved.variant?.imageUrl ? resolved.variant.imageUrl.trim() : ''
    if (vImg && !baseUrls.includes(vImg)) return [vImg, ...baseUrls]
    return baseUrls.length ? baseUrls : galleryUrls(product)
  }, [normalized.images, product, resolved])

  useEffect(() => {
    if (!resolved.ok) return
    const vImg = resolved.variant?.imageUrl?.trim()
    if (!vImg) return
    const idx = urls.findIndex((u) => u === vImg)
    if (idx >= 0) setImageIdx(idx)
  }, [resolved.variant?.id, resolved.ok, urls])

  const mainImg = urls[imageIdx] ?? urls[0] ?? ''

  const currentPrice = useMemo(() => {
    if (!normalized.hasVariants) return normalized.price
    if (resolved.ok && resolved.variant?.price != null) return Number(resolved.variant.price)
    return normalized.price
  }, [normalized, resolved])

  const compareAt = useMemo(() => {
    if (!normalized.hasVariants) return normalized.compareAtPrice
    const vid = resolved.ok ? resolved.variant?.id : null
    const pv = product.variants?.find((v) => v.id === vid)
    const c = pv?.compare_at_price != null ? Number(pv.compare_at_price) : null
    if (c != null && Number.isFinite(c) && c > 0) return c
    return null
  }, [normalized, resolved, product.variants])

  const hasDiscount =
    compareAt != null && Number.isFinite(compareAt) && compareAt > currentPrice
  const discountPct = useMemo(() => {
    if (compareAt == null || !Number.isFinite(compareAt)) return null
    return computeDiscount(currentPrice, compareAt)?.pct ?? null
  }, [compareAt, currentPrice])

  const priceLabel = normalized.hasVariants
    ? formatTnd(currentPrice)
    : listProductDisplayPrice(product)

  const storeName = (refStore?.store?.name ?? '').trim() || 'Boutique'
  const to = `${base}/product/${encodeURIComponent(product.slug || product.id)}`
  const blurb = (product.meta_description ?? '').trim().slice(0, 220)

  const hasVariantRows = (product.variants?.length ?? 0) > 0
  const variantDataMissing =
    product.has_variants &&
    (!hasVariantRows ||
      !resolved.ok ||
      (needsVariantChoiceUI && optionKeys.length === 0))
  const canAdd = !variantDataMissing && canLineItemAdd(normalized, resolved)

  const freeShippingThreshold = useMemo(() => {
    const cfg = (refStore?.store as { layout_config?: { checkout?: { free_shipping_threshold?: unknown } } })
      ?.layout_config
    const val = cfg?.checkout?.free_shipping_threshold
    const n = Number(val)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [refStore?.store])

  const stockLine = useMemo(() => {
    if (normalized.isDropshipping) {
      return 'Expédié par le fournisseur — délai indiqué au paiement.'
    }
    if (!normalized.trackInventory) {
      return 'En stock — commandez tant qu’il en reste.'
    }
    if (normalized.hasVariants && resolved.variant) {
      const q = resolved.variant.inventoryQuantity ?? resolved.variant.stock ?? 0
      if (resolved.variant.status !== 'active') return 'Indisponible pour cette option.'
      if (q <= 0) return 'Rupture de stock pour cette option.'
      return `${q} en stock — expédition rapide selon disponibilité.`
    }
    if (!normalized.hasVariants) {
      if (normalized.stock <= 0 || normalized.status === 'out_of_stock') {
        return 'Rupture de stock.'
      }
      return `${normalized.stock} en stock — expédition rapide selon disponibilité.`
    }
    return 'Disponibilité confirmée au paiement.'
  }, [normalized, resolved])

  const stockPositive =
    normalized.isDropshipping ||
    !normalized.trackInventory ||
    (normalized.hasVariants && resolved.variant
      ? resolved.variant.status === 'active' &&
        (resolved.variant.inventoryQuantity ?? resolved.variant.stock ?? 0) > 0
      : normalized.stock > 0 && normalized.status !== 'out_of_stock')

  const descriptionBody =
    (product.meta_description && product.meta_description.trim()) ||
    (normalized.description && normalized.description.trim()) ||
    'Qualité sélectionnée pour le quotidien. Ouvrez la fiche complète pour mesures, entretien et photos.'

  const materialsBody =
    product.product_type === 'digital'
      ? 'Contenu numérique — pas d’envoi physique. Les accès sont envoyés après achat.'
      : product.product_type === 'service'
        ? 'Prestation de service — le détail dépend de la réservation. Voir la page produit.'
        : 'Composition et entretien sur l’étiquette. En cas de doute : lavage à froid et séchage à l’air.'

  const deliveryBody =
    normalized.needsShipping === false
      ? 'Pas d’expédition — livraison numérique ou sur place selon la fiche produit.'
      : 'Nous préparons les commandes rapidement. Vous recevrez un suivi dès l’expédition. Les délais dépendent de votre région et du transporteur.'

  const runAdd = useCallback(async () => {
    if (!refStore) {
      navigate(to)
      onClose()
      return
    }
    if (variantDataMissing || !canLineItemAdd(normalized, resolved)) {
      navigate(to)
      onClose()
      return
    }
    setCartPhase('loading')
    await Promise.resolve()
    const v = product.has_variants ? resolved.variant : null
    const vid = product.has_variants ? resolved.variantId : null
    const r = addToCart(refStore.cart, normalized, v, vid, qty)
    if (!r.ok) {
      setCartPhase('idle')
      navigate(to)
      onClose()
      return
    }
    refStore.setCart(r.cart)
    setCartPhase('success')
    window.setTimeout(() => {
      setCartPhase('idle')
      onClose()
      refStore.openCartDrawer()
    }, 550)
  }, [
    refStore,
    variantDataMissing,
    normalized,
    resolved,
    navigate,
    to,
    onClose,
    product.has_variants,
    qty,
  ])

  const pickOption = (key: string, val: string) => {
    setSelectedOptions((prev) => mergeSelectionOnPick(normalized, prev, key, val))
  }

  const bumpQty = (d: number) => setQty((q) => Math.max(1, Math.min(99, q + d)))

  const isMobileSheet = side === 'bottom'
  const addDisabled =
    cartPhase === 'loading' || cartPhase === 'success' || variantDataMissing || !canAdd

  const priceBlock: ReactNode = (
    <div className="flex flex-wrap items-baseline gap-2">
      {hasDiscount && compareAt != null ? (
        <p className="text-sm font-semibold tabular-nums text-red-600 line-through">{formatTnd(compareAt)}</p>
      ) : null}
      <p className="text-lg font-bold tabular-nums text-foreground">{priceLabel}</p>
    </div>
  )

  const qtyControl = (
    <div className="inline-flex items-center rounded-full border border-border bg-card px-1">
      <button
        type="button"
        disabled={qty <= 1 || cartPhase === 'loading'}
        onClick={() => bumpQty(-1)}
        className="flex min-h-11 min-w-11 items-center justify-center text-foreground disabled:opacity-40"
        aria-label="Diminuer la quantité"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="min-w-[2rem] text-center text-sm font-bold tabular-nums text-foreground">{qty}</span>
      <button
        type="button"
        disabled={qty >= 99 || cartPhase === 'loading'}
        onClick={() => bumpQty(1)}
        className="flex min-h-11 min-w-11 items-center justify-center text-foreground disabled:opacity-40"
        aria-label="Augmenter la quantité"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  )

  const addButtonLabel =
    cartPhase === 'loading'
      ? 'Ajout…'
      : cartPhase === 'success'
        ? 'Ajouté'
        : variantDataMissing
          ? 'Choisir les options'
          : !canAdd
            ? 'Indisponible'
            : 'Ajouter au panier'

  const addButtonClass = cn(
    'h-12 w-full rounded-none border-0 text-sm font-bold uppercase tracking-wide text-white disabled:opacity-50',
    cartPhase === 'success' ? 'bg-emerald-700 hover:bg-emerald-700' : 'bg-foreground hover:bg-foreground'
  )

  const renderPrimaryAdd = () => (
    <Button
      type="button"
      variant="primary"
      className={addButtonClass}
      disabled={addDisabled}
      onClick={() => void runAdd()}
      aria-label={addButtonLabel}
    >
      {cartPhase === 'success' ? (
        <span className="inline-flex items-center gap-2">
          <Check className="h-4 w-4" aria-hidden />
          Ajouté
        </span>
      ) : (
        addButtonLabel
      )}
    </Button>
  )

  return (
    <>
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto',
          isMobileSheet && 'pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:pb-0'
        )}
      >
        <div
          className={cn(
            'relative aspect-[3/4] w-full bg-muted sm:aspect-[4/5]',
            staggerIn
          )}
        >
          {mainImg ? (
            <img src={mainImg} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Aucune image</div>
          )}
          {hasDiscount && discountPct != null ? (
            <span className="absolute left-3 top-3 bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
              Promo -{discountPct}%
            </span>
          ) : null}
          {urls.length > 1 ? (
            <>
              <button
                type="button"
                aria-label="Image suivante"
                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow-md ring-1 ring-border/80 transition hover:bg-card"
                onClick={() => setImageIdx((i) => (i + 1) % urls.length)}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                {urls.slice(0, 8).map((u, i) => (
                  <button
                    key={`${u}-${i}`}
                    type="button"
                    aria-label={`Image ${i + 1} sur ${Math.min(urls.length, 8)}`}
                    className={cn(
                      'h-1.5 w-1.5 rounded-full transition',
                      i === imageIdx ? 'bg-card' : 'bg-card/50'
                    )}
                    onClick={() => setImageIdx(i)}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className={cn('space-y-5 px-5 pb-8 pt-5', staggerIn, 'motion-safe:delay-100')}>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{storeName}</p>
            <h2 className="mt-1 text-xl font-bold uppercase tracking-tight text-foreground">{product.name}</h2>
            {blurb ? <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{blurb}</p> : null}
            <div className="mt-3 hidden sm:block">{priceBlock}</div>
            <p className="mt-1 hidden text-xs text-muted-foreground sm:block">TVA incluse le cas échéant</p>
          </div>

          {product.has_variants && needsVariantChoiceUI && hasVariantRows && optionKeys.length > 0 ? (
            <div className="space-y-4">
              {optionKeys.map((key) => {
                const values = availableValuesForKey(normalized, selectedOptions, key)
                const isSize = /size|taille/i.test(key)
                const isColor = detectColorOption(key, values)
                return (
                  <div key={key}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-foreground">{key}</span>
                      {isSize ? (
                        <Link
                          to={`${to}#size-chart`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                          onClick={onClose}
                        >
                          <Ruler className="h-3.5 w-3.5" aria-hidden />
                          Guide des tailles
                        </Link>
                      ) : null}
                    </div>
                    {isColor ? (
                      <ColorSwatchSelector
                        optionLabel={key}
                        items={values.map((val) => ({
                          value: val,
                          available: true,
                          trustedHex: trustedHexFromVariants(product.variants, key, val),
                        }))}
                        selectedValue={selectedOptions[key]}
                        onSelect={(val) => pickOption(key, val)}
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2" role="listbox" aria-label={key}>
                        {values.map((val) => {
                          const active = selectedOptions[key] === val
                          return (
                            <button
                              key={val}
                              type="button"
                              role="option"
                              aria-selected={active}
                              onClick={() => pickOption(key, val)}
                              className={cn(
                                'flex min-h-11 min-w-11 items-center justify-center border-2 px-3 text-sm font-semibold transition',
                                active
                                  ? 'border-border bg-card text-foreground'
                                  : 'border-border bg-muted text-foreground hover:border-border',
                              )}
                            >
                              {val}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : null}

          {variantDataMissing && product.has_variants ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Les options de ce produit sont disponibles sur la fiche complète.
            </p>
          ) : null}

          <p
            className={cn(
              'flex flex-wrap items-center gap-2 text-xs font-medium',
              stockPositive ? 'text-emerald-700' : 'text-muted-foreground'
            )}
          >
            {stockPositive ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
            <span>{stockLine}</span>
            <Info className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </p>

          <div className="hidden items-center gap-4 sm:flex">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Qté</span>
            {qtyControl}
          </div>

          <div className="hidden sm:block">{renderPrimaryAdd()}</div>

          <div className="rounded-lg bg-muted px-3 py-3 text-xs leading-relaxed text-foreground">
            Les informations peuvent varier selon l’article. Les mesures sont sur la fiche complète lorsqu’elles sont
            disponibles. Choisissez votre taille habituelle sauf indication contraire.
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-border px-3 py-3">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-foreground" aria-hidden />
            <p className="text-xs leading-relaxed text-foreground">
              Paiement <span className="font-semibold">sécurisé</span> : vos données sont chiffrées pendant
              la transaction.
            </p>
          </div>

          <ul className="space-y-2 text-xs text-foreground">
            <li className="flex gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              <span>
                {freeShippingThreshold != null
                  ? `Livraison gratuite dès ${formatTnd(freeShippingThreshold)}.`
                  : 'Frais de livraison affichés au paiement.'}
              </span>
            </li>
            <li className="flex gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              <span>Préparation rapide pour les articles en stock — suivi à l’expédition.</span>
            </li>
          </ul>

          <Accordion type="single" collapsible defaultValue="description" className="border-t border-border">
            <AccordionItem value="description" className="border-border">
              <AccordionTrigger className="text-xs font-bold uppercase tracking-wide text-foreground hover:no-underline">
                Description
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{descriptionBody}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="materials" className="border-border">
              <AccordionTrigger className="text-xs font-bold uppercase tracking-wide text-foreground hover:no-underline">
                Matière et entretien
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{materialsBody}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="delivery" className="border-border border-b-0">
              <AccordionTrigger className="text-xs font-bold uppercase tracking-wide text-foreground hover:no-underline">
                Livraison
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{deliveryBody}</AccordionContent>
            </AccordionItem>
          </Accordion>

          <Link
            to={to}
            className="block pb-1 text-center text-sm font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground"
            onClick={onClose}
          >
            Voir la fiche complète
          </Link>
        </div>
      </div>

      {isMobileSheet ? (
        <div
          className="fixed inset-x-0 bottom-0 z-dv-modal-content flex flex-col gap-2 border-t border-border bg-card/95 px-3 pt-2 shadow-[0_-8px_30px_-12px_rgb(0_0_0/0.2)] backdrop-blur-sm sm:hidden"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="flex items-center justify-between gap-2">
            {priceBlock}
            {qtyControl}
          </div>
          {renderPrimaryAdd()}
        </div>
      ) : null}
    </>
  )
}

export function ProductQuickViewDrawer({
  productId,
  product,
  open,
  isLoading,
  onClose,
}: {
  /** Stable id for a11y / analytics; drawer body uses fetched `product` when ready */
  productId: string | null
  product: Product | null
  open: boolean
  isLoading: boolean
  onClose: () => void
}) {
  const side = useDrawerSide()

  if (!open) return null

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <SheetContent
        side={side}
        overlayClassName={cn('bg-black/40', motionReduceOverlay)}
        className={cn(
          'flex w-full flex-col border-border bg-card p-0',
          motionReducePanel,
          side === 'bottom' && 'max-h-[min(92dvh,920px)] rounded-t-2xl sm:max-w-none',
          side === 'right' && 'h-full sm:max-w-md'
        )}
        aria-describedby={undefined}
        data-quick-view-product-id={productId ?? undefined}
      >
        <SheetTitle className="sr-only">
          {product?.name ?? (isLoading ? 'Chargement du produit…' : 'Aperçu rapide')}
        </SheetTitle>

        {isLoading || !product ? (
          <QuickViewSkeleton side={side} />
        ) : (
          <ProductQuickViewDrawerBody product={product} side={side} onClose={onClose} />
        )}
      </SheetContent>
    </Sheet>
  )
}
